import Container from '@/components/Container/Container';
import { Typography, getColor } from '@/shared';
import { SortByType, getSortedContent } from '@/shared/helpers/sort';
import { ColorContext } from '@/shared/providers/ColorContext';
import { useThemeSection } from '@/shared/providers/SectionContext';
import { JobStoryblok } from '@/shared/types/storyblok/storyblok-types';
import { Icon } from '@/shared/ui/Icon/Icon';
import clsx from 'clsx';
import Link from 'next/link';
import { useContext, useEffect, useState } from 'react';
import styles from './JobListing.module.scss';

type JobListingCoreProps = {
    content: JobStoryblok[];
    sortBy: SortByType;
    loadMoreButton: string;
    startingItems?: number;
};

const JobListingCore = (props: JobListingCoreProps) => {
    const theme = useThemeSection();
    const isDark = theme === 'Dark';
    const hexColor = useContext(ColorContext);
    const { content: originalContent, sortBy, loadMoreButton, startingItems = 100 } = props || {};
    const [content, setContent] = useState(originalContent);
    const [shownCount, setShownCount] = useState(startingItems);
    const remainingItems = Math.min(3, content?.length - shownCount);

    const replaceNumber = (text: string, number: number): string => {
        return text ? text.replace('{number}', String(number)) : 'Load More';
    };

    useEffect(() => {
        let sortedContent = [...originalContent];
        if (originalContent) {
            if (sortBy) {
                sortedContent = getSortedContent(sortedContent, sortBy);
            }
        }

        setContent(sortedContent);
    }, [sortBy, originalContent]);

    return (
        <Container code="fullpage" className={{ wrap: clsx(styles.root, { [styles.dark]: isDark }) }}>
            {content &&
                content.slice(0, shownCount).map((element: JobStoryblok, index: number) => {
                    const isLastRow = index === content.slice(0, shownCount).length - 1;
                    const { title, rolePercentage, jobLocation, jobField } = element?.content || {};
                    return (
                        <div key={element.id} className={clsx(styles.row, { [styles.lastRow]: isLastRow })}>
                            <Link href={element.full_slug}>
                                <Container code="standard" className={{ wrap: styles.wrapper }}>
                                    <div>
                                        {title && (
                                            <Typography.H3 darkTheme={isDark} className={styles.title}>
                                                {title}
                                            </Typography.H3>
                                        )}
                                        {rolePercentage && (
                                            <div style={{ marginBottom: '12px' }}>
                                                <Typography.Small darkTheme={isDark} className={styles.role}>
                                                    {index === content.length - 1 ? rolePercentage : rolePercentage}
                                                </Typography.Small>
                                            </div>
                                        )}
                                        {(jobLocation?.name || jobField?.name) && (
                                            <Typography.P darkTheme={isDark} className={styles.location}>
                                                {jobLocation.name && jobLocation.name}{' '}
                                                {jobField.name && jobField.name && '·'} {jobField.name && jobField.name}
                                            </Typography.P>
                                        )}
                                    </div>
                                    <Icon name="arrow_forward" className={styles.icon} />
                                </Container>
                            </Link>
                        </div>
                    );
                })}
            {content && shownCount < content?.length && (
                <div
                    className={styles.button}
                    style={{ borderColor: getColor(hexColor, isDark) }}
                    onClick={() => setShownCount((prevState: number) => prevState + startingItems)}
                >
                    {replaceNumber(loadMoreButton, remainingItems)}
                </div>
            )}
        </Container>
    );
};

export default JobListingCore;
